import { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { Box, Card, Typography, Divider, useTheme, useColorScheme } from "@mui/joy";
import { useProject } from "../../../../hooks/query";

function getMonthLabel(dateStr: string): string {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export default function Analytics() {
    const chartRef = useRef<HTMLDivElement | null>(null);
    const { mode, setMode } = useColorScheme();

    const { useList } = useProject();
    const listProjectSvc = useList();

    useEffect(() => {
        listProjectSvc.mutateAsync().catch(console.error);
    }, []);

    useEffect(() => {
        if (!chartRef.current || !listProjectSvc.data) return;

        const projects = listProjectSvc.data;
        const currentYear = new Date().getFullYear();

        // Filter data
        const estimatedThisYear = projects.filter(
            (p: any) => new Date(p.EstimatedEndDate).getFullYear() === currentYear
        );
        const actualThisYear = projects.filter((p: any) => {
            const actualEndDate = new Date(p.ActualEndDate);
            const isThisYear = actualEndDate.getFullYear() === currentYear;
            const summary = p.TaskSummary || {};
            const openTasks = (summary["NOT-STARTED"] || 0) + (summary["IN-PROGRESS"] || 0);
            return isThisYear && openTasks === 0;
        });

        const monthMap: Record<string, { estimated: number; actual: number }> = {};

        for (let i = 0; i < 12; i++) {
            const month = `${currentYear}-${String(i + 1).padStart(2, "0")}`;
            monthMap[month] = { estimated: 0, actual: 0 };
        }

        estimatedThisYear.forEach((p: any) => {
            const key = getMonthLabel(p.EstimatedEndDate);
            if (monthMap[key]) monthMap[key].estimated += 1;
        });

        actualThisYear.forEach((p: any) => {
            const key = getMonthLabel(p.ActualEndDate);
            if (monthMap[key]) monthMap[key].actual += 1;
        });

        const months = Object.keys(monthMap);
        const estimatedData = months.map((m) => monthMap[m].estimated);
        const actualData = months.map((m) => monthMap[m].actual);

        const chart = echarts.init(chartRef.current, mode === "dark" ? "dark" : "light");
        chart.setOption({
            backgroundColor: "transparent",
            textStyle: {
                fontFamily: "Albert Sans", // Replace with your desired font
            },
            tooltip: { trigger: "axis" },
            legend: {
                data: ["Estimated End", "Actual End"],
                orient: "vertical",
                left: "left",
                top: "20px",
                itemGap: 20,
                itemWidth: 25, // width of symbol
                textStyle: {
                    fontWeight: 400,
                    fontSize: 14,
                    padding: [0, 0, 0, 20],
                },
            },
            xAxis: {
                type: "category",
                axisLabel: {
                    fontSize: 14,
                    fontWeight: 500,
                },
                splitLine: {
                    show: false,
                },
                data: months.map((m) => {
                    const monthNum = parseInt(m.split("-")[1], 10);
                    return new Date(currentYear, monthNum - 1).toLocaleString("default", { month: "short" });
                }),
            },
            yAxis: {
                type: "value",
                splitLine: {
                    show: false,
                },
                axisLabel: {
                    fontSize: 14,
                    fontWeight: 500,
                },
            },
            series: [
                {
                    name: "Estimated End",
                    type: "line",
                    data: estimatedData,
                    itemStyle: { color: "#5fbd92" },
                    lineStyle: { width: 3 },
                    smooth: true,
                },
                {
                    name: "Actual End",
                    type: "bar",
                    data: actualData,
                    itemStyle: { color: "#2f6e3b", borderRadius: [6, 6, 0, 0] },
                    barWidth: "40%",
                },
            ],
            grid: {
                left: 300,
                top: 50,
                bottom: 30,
                right: 20,
            },
        });

        return () => chart.dispose();
    }, [listProjectSvc.data, mode]);

    return (
        <Card sx={{ mt: 2, p: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography level="title-lg">Project Completion Trend</Typography>

            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 300,
                    mt: "auto", // push it to the bottom
                }}
            />
        </Card>
    );
}
